/**
 * Create Template Dialog
 *
 * Controlled dialog that creates an environment template, optionally from a
 * GitHub repo.
 * Extracted from the old TemplateListSection so the same dialog can mount
 * from multiple places (empty-state hero, list-view "New Template" button,
 * etc.). The instance-size Select stays here so users pick up-front.
 */
import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Flex,
  Select,
  TextArea,
  TextField,
  Text,
  type SelectOption,
} from '@chatsdet/ui';
import { Warning } from '@phosphor-icons/react';
import { DEFAULT_INSTANCE_SIZE, parseInstanceSize, type InstanceSize } from '@chatsdet/types';
import { INSTANCE_SIZE_OPTIONS } from '../instance-size-options';
import { useCreateTemplate } from '../../../hooks/useEnvironmentTemplates';

type CreateFormState = {
  name: string;
  repositoryFullName: string;
  branch: string;
  subdirectory: string;
  hints: string;
  instanceSize: InstanceSize;
};

const NO_REPOSITORY_OPTION = '__no_repository__';

const emptyForm = (): CreateFormState => ({
  name: '',
  repositoryFullName: '',
  branch: 'main',
  subdirectory: '',
  hints: '',
  instanceSize: DEFAULT_INSTANCE_SIZE,
});

export interface CreateTemplateDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  githubRepositories?: Array<{
    id: number;
    fullName: string;
    defaultBranch: string;
  }>;
}

export function CreateTemplateDialog({
  isOpen,
  onOpenChange,
  githubRepositories = [],
}: CreateTemplateDialogProps) {
  const createMutation = useCreateTemplate();
  const [form, setForm] = React.useState<CreateFormState>(emptyForm);

  const repoOptions: SelectOption[] = React.useMemo(
    () => [
      { id: NO_REPOSITORY_OPTION, label: 'No repository (empty workspace)' },
      ...githubRepositories.map((repo) => ({
        id: repo.fullName,
        label: repo.fullName,
      })),
    ],
    [githubRepositories]
  );

  // Default the branch field whenever the user picks a repo.
  React.useEffect(() => {
    if (!form.repositoryFullName) return;
    const repo = githubRepositories.find((r) => r.fullName === form.repositoryFullName);
    if (repo) {
      setForm((prev) => ({ ...prev, branch: repo.defaultBranch }));
    }
  }, [form.repositoryFullName, githubRepositories]);

  const handleClose = React.useCallback(() => {
    onOpenChange(false);
    setForm(emptyForm());
    createMutation.reset();
  }, [onOpenChange, createMutation]);

  const handleCreate = () => {
    const repositoryFullName = form.repositoryFullName.trim();
    createMutation.mutate(
      {
        name: form.name.trim() || repositoryFullName.split('/')[1] || 'Untitled',
        ...(repositoryFullName ? { repositoryFullName } : {}),
        ...(repositoryFullName
          ? {
              branch: form.branch.trim() || 'main',
              subdirectory: form.subdirectory.trim() || undefined,
              hints: form.hints.trim() || undefined,
            }
          : {}),
        instanceSize: form.instanceSize,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          setForm(emptyForm());
        },
      }
    );
  };

  const hasRepository = Boolean(form.repositoryFullName);

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
        else onOpenChange(true);
      }}
    >
      <span />
      <Dialog title="Create Environment Template">
        <DialogContent>
          <Box className="space-y-4">
            {createMutation.isError && (
              <Flex
                align="center"
                gap="sm"
                className="p-3 rounded-md bg-destructive/10 text-destructive"
              >
                <Warning size={20} />
                <Text size="sm">
                  {createMutation.error?.message || 'Failed to create template'}
                </Text>
              </Flex>
            )}
            <Select
              label="Repository (optional)"
              items={repoOptions}
              selectedKey={form.repositoryFullName || NO_REPOSITORY_OPTION}
              onSelectionChange={(key) =>
                setForm((prev) => ({
                  ...prev,
                  repositoryFullName: key === NO_REPOSITORY_OPTION ? '' : String(key),
                }))
              }
            />
            <TextField
              label="Name"
              description="Display name for this template"
              value={form.name}
              onChange={(value) => setForm((prev) => ({ ...prev, name: value }))}
              placeholder={form.repositoryFullName.split('/')[1] || 'my-app'}
            />
            {hasRepository && (
              <>
                <TextField
                  label="Branch"
                  value={form.branch}
                  onChange={(value) => setForm((prev) => ({ ...prev, branch: value }))}
                />
                <TextField
                  label="Subdirectory (optional)"
                  description="For monorepos, specify which directory contains the app"
                  value={form.subdirectory}
                  onChange={(value) => setForm((prev) => ({ ...prev, subdirectory: value }))}
                  placeholder="apps/api"
                />
              </>
            )}
            <Box>
              <Select
                label="Instance size"
                items={INSTANCE_SIZE_OPTIONS}
                selectedKey={form.instanceSize}
                onSelectionChange={(key) => {
                  const next = parseInstanceSize(String(key));
                  if (next) setForm((prev) => ({ ...prev, instanceSize: next }));
                }}
              />
              <Text size="xs" className="text-muted-foreground mt-1">
                Choose the size for this template's sandboxes. You can change it later from the
                Configuration tab.
              </Text>
            </Box>
            {hasRepository && (
              <TextArea
                label="Hints (optional)"
                description="Help the AI agent understand how to configure this app"
                value={form.hints}
                onChange={(value) => setForm((prev) => ({ ...prev, hints: value }))}
                placeholder="Focus on the API server. Use PostgreSQL 16. The app needs Redis for caching."
              />
            )}
          </Box>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={handleClose} isDisabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onPress={handleCreate} isDisabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating...' : 'Create Template'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}

CreateTemplateDialog.displayName = 'CreateTemplateDialog';
